import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SpiritCard from "@/components/SpiritCard";
import WineDetailDialog from "@/components/WineDetailDialog";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Info, ArrowUp, X, Home as HomeIcon } from "lucide-react";
import BackButton from "@/components/BackButton";
import { useNavigate } from "react-router-dom";
import { useStaffStockVisibility, StaffStockToggle } from "@/hooks/useStaffStockVisibility";

// ---------------------------------------------------------------------------
//  State model (FIX 8.9i — refactor deterministico dopo la QUARTA regressione)
// ---------------------------------------------------------------------------
//
// UNA SOLA FONTE DI VERITÀ per i filtri di questa pagina:
//   - `search`     (string): testo cercato
//   - `categoria`  (chip):   uno dei valori di CATEGORY_TABS
//   - `produttore` (dropdown "Tutti i produttori"):
//        SESSION-ONLY, MAI persistito in localStorage.
//        Motivo: il produttore è context-specific rispetto al chip
//        (es. "Az. Agr. Orterie" ha senso in "Orterie", ma se lo persistiamo
//        e l'utente clicca "Distillati", il filtro sopravvive e restringe
//        a 3 record inducendo il classico "sono spariti tutti i distillati").
//        Il deep link `?produttore=X` continua a funzionare per condivisione,
//        ma non lascia traccia in storage.
//
// TRANSIZIONI DETERMINISTICHE:
//   Ogni click su un chip azzera SEMPRE `produttore` e ricalcola il
//   query set da zero. Nessun merge incrementale di stato precedente.
//   La query API è funzione pura di (search, categoria, produttore).
//
// SCHEMA STORAGE `co_spirits_state_v2`:
//   { "search": "", "categoria": "non_vino" }
//   Chiave nuova (v2) per invalidare eventuali dati sporchi della vecchia
//   `co_spirits_state` (v1) che portavano un `produttore` residuo.
// ---------------------------------------------------------------------------
const STORAGE_KEY = "co_spirits_state_v2";
const LEGACY_STORAGE_KEY = "co_spirits_state";
const PAGE_SIZE = 30;

// Public non-wine sections. Mirrors NON_WINE_CATEGORIES in backend/models.py.
// La pseudo-categoria "orterie" filtra i prodotti della casa (regex
// case-insensitive sul producer + pattern "coll N" nel raw Mexal).
const CATEGORY_TABS = [
    { value: "non_vino", label: "Tutto" },
    { value: "orterie", label: "Orterie", featured: true },
    { value: "distillato", label: "Distillati" },
    { value: "liquore", label: "Liquori" },
    { value: "birra", label: "Birre" },
    { value: "altro", label: "Altro" },
];
const CATEGORY_VALUES = new Set(CATEGORY_TABS.map((t) => t.value));

function loadStoredState() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Validazione: se qualcosa è incoerente → reset ai default
        const search = typeof parsed?.search === "string" ? parsed.search : "";
        const categoria = CATEGORY_VALUES.has(parsed?.categoria)
            ? parsed.categoria
            : "non_vino";
        return { search, categoria };
    } catch (e) {
        return null;
    }
}

export default function Spirits() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [urlParams] = useSearchParams();

    // Migrazione one-shot: cancella lo storage v1 (poteva contenere
    // `produttore` residuo che innescava il bug 8.9i).
    useEffect(() => {
        try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch (e) {}
    }, []);

    const initial = useMemo(() => {
        const stored = loadStoredState() || { search: "", categoria: "non_vino" };
        const urlCat = urlParams.get("categoria");
        const search = urlParams.get("q") ?? stored.search;
        const categoria = CATEGORY_VALUES.has(urlCat) ? urlCat : stored.categoria;
        // `produttore`: SOLO da URL (deep link/share). Mai da storage.
        const produttore = urlParams.get("produttore") ?? "";
        return { search, categoria, produttore };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [search, setSearch] = useState(initial.search);
    const [categoria, setCategoria] = useState(initial.categoria);
    const [produttore, setProduttore] = useState(initial.produttore);
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [firstLoad, setFirstLoad] = useState(true);
    const [facets, setFacets] = useState(null);
    const [openItem, setOpenItem] = useState(null);
    const sentinelRef = useRef(null);
    const skipRef = useRef(0);

    const isAnon = !user;
    const isUnverified = user && !user.email_verified;
    const canSeePrices = true;  // Fase 6.8: listino pubblico sempre visibile.
    const isTradeVipUnverified = isUnverified && (user?.role === "trade" || user?.role === "vip");
    const isStaff = user && (user.role === "staff" || user.role === "admin");
    // FIX 8.9j-P1 · toggle giacenze staff (persistito in localStorage).
    const { showStock, toggle: toggleShowStock } = useStaffStockVisibility();

    useEffect(() => {
        const h = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(h);
    }, [search]);

    // Persistenza: SOLO search + categoria. Mai produttore.
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ search, categoria }));
        } catch (e) {}
    }, [search, categoria]);

    // Query builder: funzione pura di (search, categoria, produttore).
    // Nessun merge implicito di stato precedente.
    const buildParams = useCallback(() => {
        // Pseudo-categoria "orterie" → non_vino + produttore_regex hard-coded.
        // Il produttore_regex vive SOLO qui, non viene mai persistito.
        if (categoria === "orterie") {
            const p = { limit: PAGE_SIZE, categoria: "non_vino", produttore_regex: "orterie" };
            if (debouncedSearch) p.q = debouncedSearch;
            if (produttore) p.produttore = produttore;
            return p;
        }
        const p = { limit: PAGE_SIZE, categoria };
        if (debouncedSearch) p.q = debouncedSearch;
        if (produttore) p.produttore = produttore;
        return p;
    }, [categoria, debouncedSearch, produttore]);

    // Producers list: cross-facet dai facets del set corrente.
    const producers = useMemo(() => {
        const list = facets?.facets?.producers || [];
        return list.map((p) => ({ value: p.value, count: p.count }));
    }, [facets]);

    useEffect(() => {
        const params = buildParams();
        setLoading(true);
        skipRef.current = 0;
        Promise.all([
            api.get("/wines", { params: { ...params, skip: 0 } }),
            api.get("/wines/facets", { params: { ...params, skip: undefined, limit: undefined } }),
        ])
            .then(([{ data }, { data: fdata }]) => {
                setItems(data.items);
                setTotal(data.total);
                setHasMore(data.has_more);
                skipRef.current = data.items.length;
                setFacets(fdata);
            })
            .finally(() => {
                setLoading(false);
                setFirstLoad(false);
            });
    }, [buildParams]);

    // Auto-cleanup: se `produttore` è settato via deep link e nel set
    // corrente non esiste, resettalo (evita "0 risultati per un URL
    // stantio"). Non è più strettamente necessario dopo il refactor —
    // ma è un cinturone di sicurezza contro deep link obsoleti.
    useEffect(() => {
        if (!facets || !produttore) return;
        const available = (facets.facets?.producers || []).map((p) => p.value);
        if (available.length > 0 && !available.includes(produttore)) {
            setProduttore("");
        }
    }, [facets, produttore]);

    useEffect(() => {
        if (!sentinelRef.current) return;
        const el = sentinelRef.current;
        const io = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (!entry.isIntersecting || loading || !hasMore) return;
                setLoading(true);
                api
                    .get("/wines", { params: { ...buildParams(), skip: skipRef.current } })
                    .then(({ data }) => {
                        setItems((prev) => [...prev, ...data.items]);
                        setHasMore(data.has_more);
                        skipRef.current += data.items.length;
                    })
                    .finally(() => setLoading(false));
            },
            { rootMargin: "300px" }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [hasMore, loading, buildParams]);

    // Transizione chip: sempre reset produttore + set categoria. Nessuna
    // eccezione. Nessun merge. Deterministico.
    const switchTab = useCallback((target) => {
        setProduttore("");
        setCategoria(target);
    }, []);

    // Fase 6.8: listino pubblico sempre visibile — nessun banner invasivo.
    let notice = null;

    return (
        <main>
            <section className="co-container pt-6 sm:pt-8 pb-4">
                <BackButton testid="spirits-back-btn" />
            </section>
            <section className="co-container pt-2 sm:pt-4 pb-6">
                <div className="max-w-3xl">
                    <span className="co-caption block mb-3">Casa Orterie · Stazzona</span>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[hsl(var(--foreground))]">
                        Distillati <em className="italic text-[hsl(var(--primary))]">&amp; Liquori</em>
                    </h1>
                    <p className="mt-5 text-[hsl(var(--muted-foreground))] max-w-xl leading-relaxed">
                        Una piccola selezione di distillati, liquori e birre per accompagnare aperitivi,
                        dopo-cena e momenti di degustazione.
                    </p>
                </div>
            </section>

            <section
                className="co-container sticky top-20 z-20 bg-[hsl(var(--background))]/95 backdrop-blur-md py-4 co-hairline -mx-5 sm:-mx-8 lg:-mx-10 px-5 sm:px-8 lg:px-10"
                data-testid="spirits-toolbar"
            >
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                        <div className="relative flex-1">
                            <Search
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]"
                                strokeWidth={1.5}
                            />
                            <Input
                                data-testid="spirits-search-input"
                                placeholder="Cerca per nome, produttore, note…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 bg-white h-11"
                            />
                        </div>
                        <Select
                            value={produttore || "__all"}
                            onValueChange={(v) => setProduttore(v === "__all" ? "" : v)}
                        >
                            <SelectTrigger
                                data-testid="spirits-producer-filter"
                                className="w-full sm:w-56 bg-white h-11"
                            >
                                <SelectValue placeholder="Produttore" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="__all">Tutti i produttori</SelectItem>
                                {produttore && !producers.some((p) => p.value === produttore) && (
                                    <SelectItem value={produttore}>
                                        <span className="flex items-center justify-between gap-4 w-full">
                                            <span>{produttore}</span>
                                            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">0</span>
                                        </span>
                                    </SelectItem>
                                )}
                                {producers.map((p) => (
                                    <SelectItem key={p.value} value={p.value}>
                                        <span className="flex items-center justify-between gap-4 w-full">
                                            <span>{p.value}</span>
                                            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{p.count}</span>
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap" data-testid="spirits-category-tabs">
                        {CATEGORY_TABS.map((tab) => {
                            const active = categoria === tab.value;
                            if (tab.featured) {
                                return (
                                    <button
                                        key={tab.value}
                                        type="button"
                                        data-testid={`spirits-tab-${tab.value}`}
                                        onClick={() => switchTab(tab.value)}
                                        className={`px-4 py-1.5 rounded-full text-sm transition-all ${
                                            active
                                                ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-sm"
                                                : "bg-white border-2 border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))]"
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                );
                            }
                            return (
                                <button
                                    key={tab.value}
                                    type="button"
                                    data-testid={`spirits-tab-${tab.value}`}
                                    onClick={() => switchTab(tab.value)}
                                    className={`px-4 py-1.5 border rounded-full text-sm transition-colors ${
                                        active
                                            ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))] bg-white"
                                            : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] bg-white hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Fase 6.8: banner rimosso — listino pubblico sempre visibile */}

            <section className="co-container py-8">
                <div className="mb-6 flex items-baseline justify-between gap-3">
                    <p className="co-caption" data-testid="spirits-total">
                        {total === 0 && !firstLoad ? "Nessun risultato" : `${total} etichett${total === 1 ? "a" : "e"}`}
                    </p>
                    {isStaff && (
                        <div className="flex items-center gap-3">
                            <p className="co-caption text-[hsl(var(--primary))]">
                                Vista Staff · {showStock ? "Giacenze visibili" : "Giacenze nascoste"}
                            </p>
                            <StaffStockToggle showStock={showStock} onToggle={toggleShowStock} />
                        </div>
                    )}
                </div>

                {!loading && !firstLoad && items.length === 0 && (
                    <div className="py-16 text-center max-w-md mx-auto" data-testid="spirits-empty-state">
                        <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[hsl(var(--secondary))]">
                            <Info className="w-5 h-5 text-[hsl(var(--muted-foreground))]" strokeWidth={1.2} />
                        </div>
                        <p className="font-serif text-2xl mb-2">Nessuna etichetta trovata</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">
                            Prova ad allargare la ricerca o cambiare categoria.
                        </p>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                    {items.map((it) => (
                        <SpiritCard
                            key={it.id}
                            item={it}
                            onOpen={() => setOpenItem(it.id)}
                            showStock={showStock}
                        />
                    ))}
                </div>

                {loading && !firstLoad && (
                    <div className="mt-8 text-center co-caption">Caricamento…</div>
                )}

                <div ref={sentinelRef} className="h-10" />

                {!hasMore && (
                    <div className="mt-8 pt-6 border-t border-[hsl(var(--border))] flex flex-col items-center gap-4 pb-4">
                        {items.length > 0 && (
                            <p className="co-caption" data-testid="spirits-end-of-list">Fine della selezione</p>
                        )}
                        <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                            {(search || produttore || categoria !== "non_vino") && (
                                <button
                                    onClick={() => {
                                        setSearch("");
                                        setProduttore("");
                                        setCategoria("non_vino");
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] rounded-full text-sm hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))] transition-colors"
                                    data-testid="spirits-clear-filters-end-btn"
                                >
                                    <X className="w-3.5 h-3.5" strokeWidth={1.5} /> Azzera filtri
                                </button>
                            )}
                            <button
                                onClick={() => navigate("/")}
                                className="inline-flex items-center gap-1.5 px-4 py-2 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
                                data-testid="spirits-home-end-btn"
                            >
                                <HomeIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Torna alla home
                            </button>
                            <button
                                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                                className="inline-flex items-center gap-1.5 px-4 py-2 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
                                data-testid="spirits-scroll-top-btn"
                            >
                                <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} /> Torna su
                            </button>
                        </div>
                    </div>
                )}
            </section>

            <WineDetailDialog
                wineId={openItem}
                open={Boolean(openItem)}
                onOpenChange={(v) => !v && setOpenItem(null)}
                canSeePrices={canSeePrices}
                isTradeVipUnverified={isTradeVipUnverified}
            />
        </main>
    );
}
